'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import Demo, {
  DemoToggleButtonGroupControl,
} from '@/components/Demo';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';
import FullWidthChild from '@/components/ContentGrids/FullWidthChild';

import { constructSnippet } from './AnchorPositioningAreas.helpers';
import PositionAreaItem from './PositionAreaItem';

type Vertical = 'top' | 'center' | 'bottom' | 'unset';
type Horizontal = 'left' | 'center' | 'right' | 'unset';

const ANCHOR_WIDTH_REM = 5;
const ANCHOR_HEIGHT_REM = 3.5;
const GRID_MAX_WIDTH_REM = 25;
const GRID_HEIGHT_REM = 12;
const OUTER_ROW_HEIGHT_REM =
  (GRID_HEIGHT_REM - ANCHOR_HEIGHT_REM) / 2; // 4.25

// CSS Grid uses 1-based indices. 'unset' spans all 3 tracks.
const VERTICAL_ROW: Record<Vertical, number | string> = {
  top: 1,
  center: 2,
  bottom: 3,
  unset: '1 / -1',
};
const HORIZONTAL_COL: Record<Horizontal, number | string> = {
  left: 1,
  center: 2,
  right: 3,
  unset: '1 / -1',
};

interface Props {
  codeOutput?: React.ReactNode;
}

function AnchorPositioningAreas({ codeOutput }: Props) {
  return (
    <FullWidthChild>
      <DemoWrapper>
        <Demo
          id="anchor-positioning-areas"
          initialValues={{
            vertical: 'top' satisfies Vertical,
            horizontal: 'center' satisfies Horizontal,
          }}
          outputWrapper={OutputWrapper}
          controlPosition="top"
          CustomGridComponent={ControlsGrid}
          controls={[
            <DemoToggleButtonGroupControl
              inline
              key="vertical"
              id="vertical"
              label="Vertical"
              options={[
                'top',
                'center',
                'bottom',
                { label: '(not set)', value: 'unset' },
              ]}
            />,
            <DemoToggleButtonGroupControl
              inline
              key="horizontal"
              id="horizontal"
              label="Horizontal"
              options={[
                'left',
                'center',
                'right',
                { label: '(not set)', value: 'unset' },
              ]}
            />,
          ]}
        >
          {({ vertical, horizontal }) => {
            const code = constructSnippet(vertical, horizontal);
            const hasAreaSet =
              vertical !== 'unset' || horizontal !== 'unset';

            // If I only set "center" for one axis, it should not stretch and instead sit perfectly in the center.
            const weirdCenterOverride =
              (horizontal === 'center' && vertical === 'unset') ||
              (horizontal === 'unset' && vertical === 'center');

            return (
              <>
                <GridContainer>
                  <HLine
                    style={{ top: `${OUTER_ROW_HEIGHT_REM}rem` }}
                  />
                  <HLine
                    style={{
                      top: `${OUTER_ROW_HEIGHT_REM + ANCHOR_HEIGHT_REM}rem`,
                    }}
                  />
                  <VLine
                    style={{
                      left: `calc((100% - ${ANCHOR_WIDTH_REM}rem) / 2)`,
                    }}
                  />
                  <VLine
                    style={{
                      left: `calc((100% + ${ANCHOR_WIDTH_REM}rem) / 2)`,
                    }}
                  />

                  <AnchorCell>
                    <PositionAreaItem stretch>
                      Anchor
                    </PositionAreaItem>
                  </AnchorCell>

                  <TargetCell
                    style={
                      hasAreaSet
                        ? {
                            gridRow: weirdCenterOverride
                              ? 2
                              : VERTICAL_ROW[vertical as Vertical],
                            gridColumn: weirdCenterOverride
                              ? 2
                              : HORIZONTAL_COL[
                                  horizontal as Horizontal
                                ],
                          }
                        : {
                            gridRow: 3,
                            gridColumn: 2,
                            marginInline: -32,
                            marginTop: 16,
                          }
                    }
                  >
                    <PositionAreaItem primary stretch>
                      Target
                    </PositionAreaItem>
                  </TargetCell>
                </GridContainer>
                {codeOutput && (
                  <SnippetWrapper>
                    <CodeSnippetProvider lang="css" code={code}>
                      {codeOutput}
                    </CodeSnippetProvider>
                  </SnippetWrapper>
                )}
              </>
            );
          }}
        </Demo>
      </DemoWrapper>
    </FullWidthChild>
  );
}

const DemoWrapper = styled.div`
  max-width: 60rem;
`;

const ControlsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(25rem, 1fr);
  justify-content: center;
  gap: 16px;
`;

const OutputWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  padding: 24px 32px 0px;

  @media (max-width: 60rem) {
    flex-direction: column;
    align-items: center;
  }
`;
const SnippetWrapper = styled.div`
  width: 100%;
  max-width: 27rem;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr ${ANCHOR_WIDTH_REM}rem 1fr;
  grid-template-rows:
    ${OUTER_ROW_HEIGHT_REM}rem
    ${ANCHOR_HEIGHT_REM}rem
    ${OUTER_ROW_HEIGHT_REM}rem;
  position: relative;
  width: 100%;
  max-width: ${GRID_MAX_WIDTH_REM}rem;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
  overflow: hidden;
`;

const HLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 0;
  border-top: 1px dashed var(--color-gray-300);
  pointer-events: none;
`;

const VLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px dashed var(--color-gray-300);
  pointer-events: none;
`;

const AnchorCell = styled.div`
  grid-row: 2;
  grid-column: 2;
  display: flex;
  align-items: stretch;
`;

const TargetCell = styled.div`
  display: flex;
  align-items: stretch;
  z-index: 1;
`;

export default AnchorPositioningAreas;
